import { FilterType as FilterOperator } from "types/enums";
import { addPcreRegexIssue } from "utils/regexValidation";
import { z } from "zod";

import { getCapTypesForContext } from "./filterEditorFactory";
import { NUMERIC_OPERATORS, REGEX_OPERATORS } from "./filterFieldOperators";
import {
    FORM_FIELD_TAG_PREFIX,
    RANGE_OPERATORS,
    TIME_OPERATORS,
    TIME_PATTERN,
    VALUE_LESS_OPERATORS,
    WEEKDAYS,
} from "./filterTreeMapper";
import { FilterContext, FilterEditorRoot, FilterRule } from "../types/filterEditor";

/** Keys are translated in the `ui.offer-filter` namespace. */
export const FILTER_ERROR = {
    fieldRequired: "validation.field_required",
    valueRequired: "validation.value_required",
    valueMinRequired: "validation.value_min_required",
    valueMaxRequired: "validation.value_max_required",
    numberRequired: "validation.number_required",
    minNotNumber: "validation.min_not_number",
    maxNotNumber: "validation.max_not_number",
    affiliateRequired: "validation.affiliate_required",
    capNotAvailable: "validation.cap_not_available",
    capNumberRequired: "validation.cap_number_required",
    timeRequired: "validation.time_required",
    timeFromRequired: "validation.time_from_required",
    timeToRequired: "validation.time_to_required",
    timeOrder: "validation.time_order",
    timeOperatorNotAvailable: "validation.time_operator_not_available",
    weekdayRequired: "validation.weekday_required",
    fieldNotInStructure: "validation.field_not_in_structure",
} as const;

export interface FilterEditorError {
    nodeId: string;
    /** Which input of the rule is wrong, e.g. `value`, `tag` or `affiliateIds` */
    field: string;
    messageKey: string;
    /** Values for the translation, e.g. the offending field tag */
    params?: Record<string, string | number>;
    /** 1-based position of the row, plus the position inside its group, e.g. `[1, 2]` = 2nd rule of the 1st group */
    location: number[];
}

/** Regex errors reuse the shared keys of `addPcreRegexIssue`, which live outside the `ui.offer-filter` namespace. */
const SHARED_ERROR_KEY_PREFIX = "forms.general-validation.";

const isNumber = (value: string): boolean => value.trim() !== "" && Number.isFinite(Number(value));

const affiliateRuleSchema = z.object({
    id: z.string(),
    kind: z.literal("affiliate"),
    mode: z.enum(["in", "not_in"]),
    affiliateIds: z.array(z.string()).min(1, FILTER_ERROR.affiliateRequired),
});

/** `fieldTags` (all `data_*` tags of the offered form structures) turns on the "field exists in the structure" check. */
const createFieldRuleSchema = (fieldTags?: ReadonlySet<string>) =>
    z
        .object({
            id: z.string(),
            kind: z.literal("field"),
            tag: z.string(),
            operator: z.nativeEnum(FilterOperator),
            value: z.string(),
            valueMax: z.string(),
        })
        .superRefine((rule, ctx) => {
            const addIssue = (path: string, message: string) => ctx.addIssue({ code: "custom", path: [path], message });

            if (rule.tag.length <= FORM_FIELD_TAG_PREFIX.length || !rule.tag.startsWith(FORM_FIELD_TAG_PREFIX)) {
                addIssue("tag", FILTER_ERROR.fieldRequired);
            } else if (fieldTags && !fieldTags.has(rule.tag)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["tag"],
                    message: FILTER_ERROR.fieldNotInStructure,
                    params: { tag: rule.tag },
                });
            }

            if (VALUE_LESS_OPERATORS.includes(rule.operator)) {
                return;
            }

            if (RANGE_OPERATORS.includes(rule.operator)) {
                if (rule.value.trim() === "") {
                    addIssue("value", FILTER_ERROR.valueMinRequired);
                } else if (!isNumber(rule.value)) {
                    addIssue("value", FILTER_ERROR.minNotNumber);
                }
                if (rule.valueMax.trim() === "") {
                    addIssue("valueMax", FILTER_ERROR.valueMaxRequired);
                } else if (!isNumber(rule.valueMax)) {
                    addIssue("valueMax", FILTER_ERROR.maxNotNumber);
                }
                return;
            }

            if (rule.value === "") {
                addIssue("value", FILTER_ERROR.valueRequired);
                return;
            }

            if (NUMERIC_OPERATORS.includes(rule.operator) && !isNumber(rule.value)) {
                addIssue("value", FILTER_ERROR.numberRequired);
            }

            if (REGEX_OPERATORS.includes(rule.operator)) {
                // BE builds `/<value>/i` and runs PCRE, same check as the CC modify/condition regexes
                addPcreRegexIssue(rule.value, ctx, ["value"]);
            }
        });

const weekdayRuleSchema = z.object({
    id: z.string(),
    kind: z.literal("weekday"),
    mode: z.enum(["in", "not_in"]),
    days: z
        .array(z.number().refine((day) => WEEKDAYS.includes(day), FILTER_ERROR.weekdayRequired))
        .min(1, FILTER_ERROR.weekdayRequired),
});

const timeRuleSchema = z
    .object({
        id: z.string(),
        kind: z.literal("time"),
        operator: z.nativeEnum(FilterOperator),
        value: z.string(),
        valueMax: z.string(),
    })
    .superRefine((rule, ctx) => {
        const addIssue = (path: string, message: string) => ctx.addIssue({ code: "custom", path: [path], message });

        if (!TIME_OPERATORS.includes(rule.operator)) {
            addIssue("operator", FILTER_ERROR.timeOperatorNotAvailable);
            return;
        }

        if (!RANGE_OPERATORS.includes(rule.operator)) {
            if (!TIME_PATTERN.test(rule.value)) {
                addIssue("value", FILTER_ERROR.timeRequired);
            }
            return;
        }

        const hasFrom = TIME_PATTERN.test(rule.value);
        const hasTo = TIME_PATTERN.test(rule.valueMax);
        if (!hasFrom) {
            addIssue("value", FILTER_ERROR.timeFromRequired);
        }
        if (!hasTo) {
            addIssue("valueMax", FILTER_ERROR.timeToRequired);
        }
        // A reversed range (22:00-06:00) is fine, BE treats it as wrapping over midnight. Only an exclusive range
        // with identical bounds can never match.
        if (hasFrom && hasTo && rule.operator === FilterOperator.IsBetween && rule.value === rule.valueMax) {
            addIssue("valueMax", FILTER_ERROR.timeOrder);
        }
    });

const createCapRuleSchema = (context: FilterContext) =>
    z
        .object({
            id: z.string(),
            kind: z.literal("cap"),
            tag: z.string(),
            operator: z.nativeEnum(FilterOperator),
            value: z.string(),
        })
        .superRefine((rule, ctx) => {
            if (!(getCapTypesForContext(context) as string[]).includes(rule.tag)) {
                ctx.addIssue({ code: "custom", path: ["tag"], message: FILTER_ERROR.capNotAvailable });
            }
            if (!isNumber(rule.value) || Number(rule.value) < 0) {
                ctx.addIssue({ code: "custom", path: ["value"], message: FILTER_ERROR.capNumberRequired });
            }
        });

const getRuleSchema = (
    kind: FilterRule["kind"],
    context: FilterContext,
    fieldTags?: ReadonlySet<string>
): z.ZodTypeAny => {
    switch (kind) {
        case "affiliate":
            return affiliateRuleSchema;
        case "cap":
            return createCapRuleSchema(context);
        case "time":
            return timeRuleSchema;
        case "weekday":
            return weekdayRuleSchema;
        default:
            return createFieldRuleSchema(fieldTags);
    }
};

/**
 * Validates the editor rows. Every rule is checked by its zod schema.
 * Each error points to one input of one rule, so the UI can list them in a modal and highlight the rows.
 */
export const validateFilterEditor = (
    root: FilterEditorRoot,
    context: FilterContext,
    fieldTags?: ReadonlySet<string>
): FilterEditorError[] => {
    const errors: FilterEditorError[] = [];

    const check = (rule: FilterRule, location: number[]) => {
        const result = getRuleSchema(rule.kind, context, fieldTags).safeParse(rule);
        if (result.success) {
            return;
        }
        result.error.issues.forEach((issue) => {
            errors.push({
                nodeId: rule.id,
                field: String(issue.path[0] ?? ""),
                messageKey: issue.message,
                ...(issue.code === "custom" && issue.params ? { params: issue.params } : {}),
                // an empty `keyPrefix` makes i18next skip the `ui.offer-filter` prefix of the translating `t`
                ...(issue.message.startsWith(SHARED_ERROR_KEY_PREFIX) ? { params: { keyPrefix: "" } } : {}),
                location,
            });
        });
    };

    root.forEach((entry, index) => {
        if (entry.item.kind === "group") {
            entry.item.rules.forEach((rule, ruleIndex) => check(rule, [index + 1, ruleIndex + 1]));
        } else {
            check(entry.item, [index + 1]);
        }
    });

    return errors;
};
